import { SetStateAction, useCallback, useContext, useMemo, useState } from "react";
import { IMeetingItem } from "../RCAMeetingTypesSettingInterfaces";
import { ApolloError, ApolloQueryResult, FetchResult, OperationVariables } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { CorporateContext } from "contexts/CorporateContext/CorporateContext";
import { LanguageCodeMap } from "constants/constants";
import { FailureDetailsEntity } from "entities/FailureDetailsEntity/FailureDetailsEntity";
import { MeetingTypeEntity } from "entities/RCA/MeetingTypeEntity/MeetingTypeEntity";
import { handleNameTranslation } from "utilities/localesUtilities";

export const useDeleteMeetingActions = (
    deleteMeeting: IMeetingItem,
    setDeleteMeeting: (value: SetStateAction<IMeetingItem | null>) => void,
    refetch: (variables?: Partial<OperationVariables> | undefined) => Promise<ApolloQueryResult<any>>,
) => {
    const { t, i18n } = useTranslation();
    const { corporations } = useContext(CorporateContext);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [newName, setNewName] = useState<string>("");
    const [replacementMeeting, setReplacementMeeting] = useState<string | number | undefined>(undefined);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarErrorMessage, setSnackbarErrorMessage] = useState("");
    const language = useMemo(() => i18n.language.slice(0, 2), [i18n.language]);
    const languageIdSelected = useMemo(() => LanguageCodeMap[language as keyof typeof LanguageCodeMap], [language]);
    const languageCode = language.toLocaleUpperCase();

    const { getExistingTranslationId } = FailureDetailsEntity.CheckForExistingTranslationId();
    const { changeMeetingType } = MeetingTypeEntity.UpdateMeetingType();
    const { addMeetingType } = MeetingTypeEntity.InsertMeetingType();
    const { deleteAndUpdateRegistries } = MeetingTypeEntity.DeleteMeetingTypeAndUpdateRegistries();
    const { isUsed, loading, apolloQueryError } = MeetingTypeEntity.ValidateMeetingTypeIsUsed(deleteMeeting.id);

    const nameTranslation = handleNameTranslation(
        i18n.language,
        deleteMeeting?.translation?.translation_names as {
            name: string;
            language_id: number;
        }[],
    );

    const errorMessage = useCallback(
        (error?: ApolloError) => {
            let message = "";

            if (error?.message.includes("unique_name_translation_corporate")) {
                message = t("rca.settings.meeting_settings.existing_name_error");
            } else if (
                error?.message.includes("translation_name_name_language_id_corporate_id_key") ||
                error?.message.includes("translation_name_name_language_id_corporate_id_key")
            ) {
                message = t("rca.settings.meeting_settings.existing_name_translation_error");
            } else {
                message = t("rca.settings.meeting_settings.generic_error");
            }

            return message;
        },
        [t],
    );

    const handleDeleteMeeting = useCallback(async () => {
        setIsSubmitting(true);
        if (isUsed) {
            if (newName) {
                const translationNameValues = {
                    language_id: languageIdSelected,
                    name: newName,
                    corporate_id: corporations[0].corporate_id,
                };
                try {
                    let response;
                    const existingTranslationId = await getExistingTranslationId(translationNameValues);
                    if (existingTranslationId) {
                        response = await changeMeetingType({
                            variables: {
                                id: deleteMeeting.id,
                                changes: { name_translation_id: existingTranslationId },
                            },
                        });
                    } else {
                        const meetingTypeValues = {
                            corporate_id: corporations[0].corporate_id,
                            translation: {
                                data: {
                                    translation_names: {
                                        data: translationNameValues,
                                    },
                                },
                            },
                        };
                        const newMeeting: FetchResult<{ insert_meeting_type: { returning: { id: number }[] } }> =
                            await addMeetingType({ variables: { meetingType: [meetingTypeValues] } });
                        response = await deleteAndUpdateRegistries({
                            variables: {
                                replacementId: newMeeting.data?.insert_meeting_type.returning[0].id,
                                idDeleted: deleteMeeting.id,
                                meetingChanges: { deleted_at: new Date() },
                            },
                        });
                    }
                    if (response) {
                        setSnackbarMessage(t("rca.settings.meeting_settings.success_update"));
                        setIsSubmitting(false);
                        refetch();
                        setDeleteMeeting(null);
                    }
                } catch (error) {
                    setIsSubmitting(false);
                    setSnackbarErrorMessage(errorMessage(error as ApolloError));
                    return;
                }
            } else if (replacementMeeting) {
                try {
                    const response = await deleteAndUpdateRegistries({
                        variables: {
                            replacementId: replacementMeeting,
                            idDeleted: deleteMeeting.id,
                            meetingChanges: { deleted_at: new Date() },
                        },
                    });
                    if (response) {
                        setIsSubmitting(false);
                        setSnackbarMessage(t("rca.settings.meeting_settings.success_update"));
                        refetch();
                    }
                } catch (error) {
                    setIsSubmitting(false);
                    console.log(error);
                    setSnackbarErrorMessage(errorMessage(error as ApolloError));
                    return;
                }
            }
        } else if (isUsed === false) {
            try {
                let response;

                response = await changeMeetingType({
                    variables: {
                        id: deleteMeeting.id,
                        changes: { deleted_at: new Date() },
                    },
                });

                if (response) {
                    setIsSubmitting(false);
                    setSnackbarMessage(t("rca.settings.meeting_settings.success_update"));
                    refetch();
                    setDeleteMeeting(null);
                }
            } catch (error) {
                setIsSubmitting(false);
                setSnackbarErrorMessage(errorMessage(error as ApolloError));
                return;
            }
        }
        setIsSubmitting(false);
    }, [
        languageIdSelected,
        corporations,
        newName,
        replacementMeeting,
        isUsed,
        changeMeetingType,
        deleteMeeting,
        setDeleteMeeting,
        errorMessage,
        getExistingTranslationId,
        deleteAndUpdateRegistries,
        addMeetingType,
        refetch,
        t,
        setIsSubmitting,
    ]);

    return {
        handleDeleteMeeting,
        setSnackbarErrorMessage,
        setSnackbarMessage,
        setReplacementMeeting,
        setNewName,
        snackbarErrorMessage,
        snackbarMessage,
        nameTranslation,
        languageCode,
        newName,
        replacementMeeting,
        isUsed,
        loading,
        error: apolloQueryError,
        isSubmitting,
    };
};
